import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = "/api";

const Section = ({ title, children, testid }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 p-4 mb-4" data-testid={testid}>
    <h3 className="text-sm font-bold text-cyan-300 mb-2">{title}</h3>
    {children}
  </div>
);

const KV = ({ k, v, testid }) => (
  <div className="flex justify-between gap-3 text-xs py-0.5 font-mono">
    <span className="text-slate-400">{k}</span>
    <span className="text-slate-100 text-right break-all" data-testid={testid}>{String(v ?? "—")}</span>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    sent: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    skipped: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    failed: "bg-red-500/20 text-red-300 border-red-500/40",
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase ${map[status] || "bg-slate-700 text-slate-300"}`}>
      {status || "—"}
    </span>
  );
};

const DebugQrFlow = () => {
  const { token } = useParams();
  const { token: jwt } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [lastTest, setLastTest] = useState(null);
  const [callEvents, setCallEvents] = useState(null);
  const [loading, setLoading] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${jwt}` } };

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/debug/qr-flow/${token}`, auth);
      setSnapshot(res.data);
    } catch (e) {
      setSnapshot({ error: e.response?.data?.detail || "fetch_failed" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, jwt]);

  useEffect(() => { refresh(); }, [refresh]);

  const runTest = async (kind) => {
    setLoading(true);
    try {
      const path = kind === "message"
        ? `${API}/debug/qr-flow/${token}/test-message`
        : `${API}/debug/qr-flow/${token}/test-call`;
      const res = await axios.post(path, {}, auth);
      setLastTest({ kind, ...res.data });
      // Auto-fetch call events for the new session
      if (res.data?.chain?.session_id) {
        const ev = await axios.get(`${API}/debug/call-events/${res.data.chain.session_id}`, auth);
        setCallEvents(ev.data);
      }
      await refresh();
    } catch (e) {
      setLastTest({ kind, error: e.response?.data?.detail || "request_failed" });
    } finally {
      setLoading(false);
    }
  };

  const refreshCallEvents = async () => {
    if (!lastTest?.chain?.session_id) return;
    try {
      const ev = await axios.get(`${API}/debug/call-events/${lastTest.chain.session_id}`, auth);
      setCallEvents(ev.data);
    } catch (_) {}
  };

  if (!snapshot) return <div className="p-8 text-slate-300">Cargando diagnostico...</div>;

  if (snapshot.error || !snapshot.found) {
    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 p-6">
        <Link to="/dashboard" className="text-cyan-400 text-sm">&larr; Dashboard</Link>
        <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
          <p className="font-bold">No se pudo cargar el diagnostico</p>
          <p className="text-sm text-slate-400 mt-1">{snapshot.error || snapshot.reason}</p>
        </div>
      </div>
    );
  }

  const c = snapshot.chain;
  const r = snapshot.resolved;
  const p = snapshot.notif_prefs;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-100 p-4 pb-20" data-testid="debug-qr-flow-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <Link to="/dashboard" className="text-cyan-400 text-sm">&larr; Dashboard</Link>
          <button onClick={refresh} className="text-xs px-3 py-1 rounded-full bg-white/10 hover:bg-white/15" data-testid="debug-refresh-btn">Refrescar</button>
        </div>

        <h1 className="text-xl font-bold mb-1">🔍 Diagnostico de QR</h1>
        <p className="text-xs text-slate-400 mb-6 font-mono">{c.qr_token_prefix}</p>

        <Section title="Resolucion del receptor (cadena REAL)" testid="section-resolution">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <KV k="Vehicle ID" v={c.vehicle_id} />
              <KV k="QR status" v={c.qr_status} />
              <KV k="Display publico" v={c.public_display} />
              <KV k="Placa" v={c.plate} />
              <KV k="Owner email" v={c.owner_email_prefix} />
            </div>
            <div>
              <KV k="vehicle.primary_contact" v={c.vehicle_primary_contact_redacted} />
              <KV k="user.phone" v={c.user_phone_redacted} />
              <KV k="user.whatsapp" v={c.user_whatsapp_redacted} />
              <KV k="prefs.whatsapp" v={p.whatsapp_number_redacted} />
              <KV k="prefs.sms" v={p.sms_number_redacted} />
            </div>
          </div>

          <div className="mt-4 p-3 rounded-lg bg-slate-900/40 border border-cyan-500/30">
            <div className="text-[11px] text-cyan-300 font-bold mb-1">SE ENVIARIA A:</div>
            <KV k="WhatsApp" v={`${r.whatsapp_redacted} (${r.source_whatsapp})`} testid="resolved-whatsapp" />
            <KV k="SMS" v={`${r.sms_redacted} (${r.source_sms})`} testid="resolved-sms" />
            <KV k="prefs.enabled" v={String(r.enabled)} testid="resolved-enabled" />
          </div>
        </Section>

        <Section title="Configuracion de Emergencia">
          <KV k="enabled" v={String(snapshot.emergency_config.enabled)} />
          <KV k="mode" v={snapshot.emergency_config.mode} />
          <KV k="contact_count" v={snapshot.emergency_config.contact_count} />
        </Section>

        <Section title="Pruebas en vivo (real Twilio)">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => runTest("message")}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-bold disabled:opacity-50"
              data-testid="debug-test-message-btn"
            >
              📩 Probar notificacion de mensaje
            </button>
            <button
              onClick={() => runTest("call")}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-bold disabled:opacity-50"
              data-testid="debug-test-call-btn"
            >
              📞 Probar notificacion de llamada
            </button>
            {lastTest?.chain?.owner_inbox_url && (
              <a
                href={lastTest.chain.owner_inbox_url}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold"
                data-testid="debug-open-inbox-link"
              >
                ↗ Abrir inbox del propietario
              </a>
            )}
            {lastTest?.chain?.session_id && (
              <button
                onClick={refreshCallEvents}
                className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-sm"
                data-testid="debug-refresh-events-btn"
              >
                ↻ Eventos signaling
              </button>
            )}
          </div>

          {lastTest && (
            <div className="mt-4 p-3 rounded-lg bg-slate-900/60 border border-white/10" data-testid="debug-last-test-result">
              <div className="text-[11px] text-slate-400 mb-1">Resultado: {lastTest.kind}</div>
              {lastTest.error ? (
                <div className="text-red-400 text-sm">Error: {lastTest.error}</div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <StatusPill status={lastTest.chain?.twilio_status || lastTest.chain?.notification_status} />
                    <span className="text-xs text-slate-300">canal: {lastTest.chain?.channel || lastTest.chain?.notification_channel}</span>
                    <span className="text-xs text-slate-300">fuente: {lastTest.chain?.receiver_source || lastTest.chain?.notification_source}</span>
                    <span className="text-xs text-slate-500">latencia: {lastTest.chain?.latency_ms}ms</span>
                  </div>
                  <KV k="Twilio SID" v={lastTest.chain?.twilio_provider_sid || "—"} />
                  <KV k="Receiver" v={lastTest.chain?.receiver_redacted || "—"} />
                  <KV k="session_id" v={lastTest.chain?.session_id} />
                  <KV k="Twilio error" v={lastTest.chain?.twilio_error || lastTest.chain?.notification_error || "—"} />
                  {lastTest.chain?.signaling_ws_path && (
                    <KV k="Signaling WS" v={lastTest.chain.signaling_ws_path} />
                  )}
                  <KV k="Inbox URL" v={lastTest.chain?.owner_inbox_url} />
                </>
              )}
            </div>
          )}
        </Section>

        {callEvents && (
          <Section title="Eventos de signaling WebRTC" testid="section-call-events">
            <KV k="session_status" v={callEvents.session_status} />
            <KV k="call_request_status" v={callEvents.call_request_status} />
            <KV k="call_webrtc_state" v={callEvents.call_webrtc_state} />
            <KV k="active_roles" v={callEvents.active_roles?.join(", ") || "(ninguno)"} />
            <KV k="event_counts" v={JSON.stringify(callEvents.event_counts || {})} />
            <div className="mt-2 max-h-60 overflow-auto">
              {(callEvents.events || []).map((e, i) => (
                <div key={i} className="text-[11px] font-mono py-0.5 border-b border-white/5">
                  <span className="text-slate-500">{e.t.slice(11, 19)}</span>{" "}
                  <span className="text-cyan-400">{e.role}</span>{" "}
                  <span className="text-emerald-300">{e.type}</span>{" "}
                  <span className="text-slate-400">{e.note}</span>
                </div>
              ))}
              {(!callEvents.events || callEvents.events.length === 0) && (
                <div className="text-slate-500 text-xs py-2">Sin eventos. Abre la sesion en otra pestaña como propietario y toca "Aceptar" para registrar conexiones WS.</div>
              )}
            </div>
          </Section>
        )}

        <Section title="Ultimos 10 logs de notificacion" testid="section-notif-logs">
          <div className="space-y-1">
            {snapshot.last_notification_logs?.map((l, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] font-mono py-1 border-b border-white/5">
                <span className="text-slate-500">{l.created_at?.slice(0, 19)}</span>
                <StatusPill status={l.status} />
                <span className="text-slate-300">{l.channel}</span>
                <span className="text-slate-400 truncate flex-1">{l.error}</span>
                <span className="text-slate-500">{l.session_id_prefix}</span>
              </div>
            ))}
            {(!snapshot.last_notification_logs || snapshot.last_notification_logs.length === 0) && (
              <div className="text-slate-500 text-xs">Sin logs todavia.</div>
            )}
          </div>
        </Section>

        <Section title="Eventos runtime [BRACKET_TAGS] (ultimos 30 de este QR)" testid="section-runtime-tags">
          <div className="max-h-72 overflow-auto space-y-0.5">
            {(snapshot.runtime_tag_events || []).slice().reverse().map((ev, i) => (
              <div key={i} className="text-[11px] font-mono py-0.5 border-b border-white/5 flex flex-wrap gap-2">
                <span className="text-slate-500">{ev.t?.slice(11, 19)}</span>
                <span className="text-emerald-300 font-bold">[{ev.tag}]</span>
                {ev.session_id && <span className="text-cyan-300">ses:{ev.session_id.slice(0, 8)}</span>}
                {ev.data && Object.keys(ev.data).length > 0 && (
                  <span className="text-slate-400 truncate flex-1" title={JSON.stringify(ev.data)}>{JSON.stringify(ev.data)}</span>
                )}
              </div>
            ))}
            {(!snapshot.runtime_tag_events || snapshot.runtime_tag_events.length === 0) && (
              <div className="text-slate-500 text-xs">Sin eventos runtime todavia. Realiza una accion en el QR y vuelve.</div>
            )}
          </div>
        </Section>

        <Section title="Ultimas 5 sesiones del vehiculo">
          <div className="space-y-1">
            {snapshot.last_sessions?.map(s => (
              <div key={s.id} className="text-[11px] font-mono flex flex-wrap gap-2 py-1 border-b border-white/5">
                <span className="text-slate-500">{s.created_at?.slice(11, 19)}</span>
                <span className="text-cyan-300">{s.type}</span>
                <span className="text-slate-300">{s.status}</span>
                {s.call_request_status && <span className="text-emerald-300">cr:{s.call_request_status}</span>}
                {s.call_webrtc_state && <span className="text-violet-300">rtc:{s.call_webrtc_state}</span>}
                <span className="text-slate-500 truncate">{s.id.slice(0, 8)}</span>
              </div>
            ))}
          </div>
        </Section>

        <p className="text-[10px] text-slate-600 text-center pt-4">
          Todos los telefonos estan redactados (+51******xxx). Logs auditables en /api/notifications/logs.
        </p>
      </div>
    </div>
  );
};

export default DebugQrFlow;
